import React, {
    FormEvent, ReactElement, useEffect, useState,
} from "react";
import { RiEdit2Line, RiQuestionAnswerLine } from "@remixicon/react";
import { useReactFlow } from "@xyflow/react";
import {
    FormField,
    FormFieldReplacementInput,
    useGetInstalledWorkflowServiceActionQuery,
    WorkflowServiceAction,
    WorkflowStep,
    WorkflowStepGroup,
    WorkflowStepParameterInput,
} from "../../../../__generated__/api";
import Loading from "../../../loading/Loading";
import FieldTypeValueDisplay from "../../../form-field/FieldTypeValueDisplay";
import Styles from "./ActionConfiguration.module.scss";
import Button from "../../../theme/button/Button";
import { useWorkflow } from "../../providers/WorkflowProvider";
import { createChildren } from "../../../workflow/Workflow";
import { ACTION_ICON_MAP, NODE_TYPE, nodeTypes } from "../../workflow/nodes";
import { ActionProps } from "../../../workflow/actions/base";
import ConditionSettings from "../../../workflow/actions/condition";
import ApprovalSettings from "../../../workflow/actions/approval";
import MapSettings from "../../../workflow/actions/map";
import TriggerConnectorSettings from "../../../workflow/actions/trigger-connector";
import FormatSettings from "../../../workflow/actions/format";
import SetTicketFieldValueSettings from "../../../workflow/actions/set-ticket-field-value";
import WebhookSettings from "../../../workflow/actions/webhook";
import InHoursOfOperationSettings from "../../../workflow/actions/in-hours-of-operation";
import RunActionSettings from "../../../workflow/actions/run-action";
import CreateTicketSettings from "../../../workflow/actions/create-ticket";
import SetRequestType from "../../../workflow/actions/set-request-type";
import MapChannelMessagesSettings from "../../../workflow/actions/map-channel-messages";
import { ReplacementGroup } from "../../../replacement-group";
import createFormFields, { ExecutorFields } from "../../../workflow/WorkflowFields";
import { useWorkflowSidebar } from "../../providers/WorkflowSidebarProvider";
import Icon from "../../../theme/icon/Icon";
import AutomaticFollowupSettings from "../../../workflow/actions/automatic-followup";
import BackButton from "./BackButton";
import EditableControl, { ControlType } from "../../../editable-text/EditableText";
import { applyTemplate } from "../../../workflow/WorkflowCardCreate";

const CONFIG_MAP: Record<string, (params: ActionProps) => ReactElement> = {
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,condition)": ConditionSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,map)": MapSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,trigger-connector)": TriggerConnectorSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-ticket-field-value)": SetTicketFieldValueSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,format)": FormatSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,webhook)": WebhookSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,is-date-in-hours-of-operation)": InHoursOfOperationSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:8824f6a945534d91bfc0a99bb3ba07eb,send-approval)": ApprovalSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,send-approval)": ApprovalSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,run-action)": RunActionSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,set-request-type)": SetRequestType,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,create-ticket)": CreateTicketSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,map-channel-messages)": MapChannelMessagesSettings,
    "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,automatic-followup)": AutomaticFollowupSettings,
};

export default function ActionConfiguration({
    actionId,
    service,
    parentGroup,
    parentStep,
}: {
    actionId?: string;
    service: WorkflowServiceAction;
    parentGroup?: WorkflowStepGroup;
    parentStep?: WorkflowStep;
}) {
    const { data: services, loading: isLoading } = useGetInstalledWorkflowServiceActionQuery({
        fetchPolicy: "network-only",
        variables: {
            id: service.id,
        },
    });

    const {
        temporaryNodeId,
        workflow,
        currentStep,
        selectedNode,
        replaceNodeInWorkflow,
        formFields,
        clearSelection,
        setServiceConfiguration,
        selectedService,
    } = useWorkflow();

    const { resetTemporaryState } = useWorkflowSidebar();

    const { getNode, updateNode } = useReactFlow();

    const [changes, setChanges] = useState<WorkflowStepParameterInput[]>([]);
    const [allowedParentInput, setAllowedParentInput] = useState<ReplacementGroup[]>([]);

    const action = services?.workflowServiceAction;

    const [cardName, setCardName] = useState(service.name);

    useEffect(() => {
        if (selectedNode) {
            setChanges(selectedNode?.parameters ?? []);
            setCardName(selectedNode?.name);
        }
    }, [selectedNode]);

    const input = createFormFields(formFields ?? [], ExecutorFields.CAN_SET);

    useEffect(() => {
        if (!parentStep) {
            return;
        }

        const itemAllowedChildren = parentStep.allowedChildren ?? parentStep.action?.allowedChildren;

        if (!itemAllowedChildren || itemAllowedChildren.isEmpty) {
            return;
        }

        const currentParentInput: ReplacementGroup[] = [];

        itemAllowedChildren.forEach(allowedChild => {
            if (allowedChild.output?.isNotEmpty) {
                const { children, creator } = createChildren();
                allowedChild.output.forEach(creator);

                if (!children.isEmpty) {
                    currentParentInput.push({
                        id: parentStep.id,
                        label: parentStep.name ?? parentStep.action?.name ?? "",
                        description: parentStep.description ?? parentStep.action?.description ?? "",
                        input: children,
                    });
                }
            }
        });

        setAllowedParentInput(currentParentInput);
    }, [parentStep]);

    const parentChildren = parentGroup?.children ?? currentStep?.children;

    const groupChildren = parentChildren?.mapFilter(groupChild => {
        const groupChildCreator = createChildren();

        if (groupChild.output?.isNotEmpty) {
            groupChild.output.forEach(groupChildCreator.creator);
        }

        if (groupChild.action?.output?.isNotEmpty) {
            groupChild.action.output.forEach(groupChildCreator.creator);
        }

        return groupChildCreator.children.isEmpty ? undefined : {
            id: groupChild.id,
            label: groupChild.name,
            description: groupChild.description ?? "",
            input: groupChildCreator.children,
        } as ReplacementGroup;
    }) ?? [];

    const editInput = input.concat(
        allowedParentInput,
        groupChildren,
    );

    const valuesMap = changes.toIdMap(field => field.value.fieldId) ?? {};

    const handleSave = (evt: FormEvent) => {
        evt.preventDefault();
        if (!temporaryNodeId) {
            return;
        }

        const name = applyTemplate(action as WorkflowServiceAction, changes);

        const card: WorkflowStep = {
            __typename: "WorkflowStep" as const,
            id: temporaryNodeId,
            actionId: action?.id,
            name,
            parameters: changes,
            description: action?.description,
            allowedChildren: action?.allowedChildren,
            service: selectedService,
            serviceId: selectedService?.id,
            action: action as WorkflowServiceAction,
        };

        const node = getNode(temporaryNodeId);

        if (!node) {
            return;
        }

        const nodeType = service.id in nodeTypes ? service.id : NODE_TYPE.ACTION_BASE;

        updateNode(temporaryNodeId, {
            type: nodeType,
            data: card,
        });

        replaceNodeInWorkflow(temporaryNodeId, card);

        setTimeout(async () => {
            await resetTemporaryState();
        }, 0);
    };

    const ActionIcon = ACTION_ICON_MAP[action?.id as keyof typeof ACTION_ICON_MAP] ?? RiQuestionAnswerLine;

    const CustomConfig = CONFIG_MAP[service.id];

    const firstStep = workflow?.steps?.[0];

    const handleCancel = async () => {
        setServiceConfiguration(null);
        resetTemporaryState();
    };

    const handleBack = () => {
        if (selectedNode) {
            clearSelection();
            resetTemporaryState();
        } else {
            setServiceConfiguration(null);
        }
    };

    if (isLoading || !action) {
        return <Loading />;
    }

    const backButtonDestination = action.integrations?.[0]?.label ?? "Actions";

    return (
        <form onSubmit={handleSave} className={Styles.container}>
            <div>

                {
                    !selectedNode && (
                        <BackButton
                            onClick={handleBack}
                            destination={backButtonDestination}
                        />
                    )
                }

                <div className={Styles["action-name"]}>
                    <div className={Styles["action-name-icon"]}>
                        <Icon
                            icon={<ActionIcon />}
                            isRounded
                            size="medium"
                            variant="primary"
                        />
                    </div>
                    <div className={Styles["action-name-text"]}>
                        <span className={Styles["action-name-text-title"]}>Action</span>
                        <EditableControl
                            type={ControlType.TEXT}
                            isFormField
                            inputClassName={Styles["editable-control"]}
                            value={cardName}
                            onChange={value => setCardName(value as string)}
                        >
                            <div className={Styles["action-name-text-name-container"]}>
                                <h3 className={Styles["action-name-text-name"]}>
                                    {cardName}
                                </h3>
                                <RiEdit2Line size={20} className={Styles["editable-control-icon"]} />
                            </div>
                        </EditableControl>
                    </div>
                </div>
                <div>
                    {(firstStep && CustomConfig) ? (
                        <CustomConfig
                            id={actionId}
                            service={service}
                            input={editInput}
                            values={changes}
                            workflow={firstStep}
                            onChange={value => {
                                const newValues = value.values.map(row => ({
                                    value: row,
                                    replacement: value.replacements,
                                }));
                                setChanges(newValues);
                            }}
                            shouldUseEditor
                        />
                    ) : (
                        action.input?.map(field => (
                            <FieldTypeValueDisplay
                                shouldUseEditor
                                key={field.id}
                                className="mt-4 mx-2"
                                inputClassName="form-control my-2"
                                field={field as FormField}
                                value={valuesMap[field.id]?.value ?? field.defaultValue}
                                replacements={valuesMap[field.id]?.replacement as FormFieldReplacementInput[]}
                                replaceableInput={editInput}
                                getCurrentForm={() => Object.values(valuesMap).map(row => row.value)}
                                onChange={(changedValue, replacements) => {
                                    const copy = Array.from(changes);
                                    const index = changes.findIndex(row => row.value.fieldId === field.id);
                                    if (index >= 0) {
                                        copy[index] = {
                                            value: changedValue,
                                            replacement: replacements,
                                        };
                                    } else {
                                        copy.push({
                                            value: changedValue,
                                            replacement: replacements,
                                        });
                                    }

                                    setChanges(copy);
                                }}
                            />
                        ))
                    )}
                </div>
            </div>
            <div className="d-flex justify-content-end gap-2">
                <Button variant="ghost-outline-blue" onClick={handleCancel}>
                    Cancel
                </Button>
                <Button variant="primary" type="submit">
                    Save
                </Button>
            </div>
        </form>
    );
}

ActionConfiguration.defaultProps = {
    actionId: undefined,
    parentGroup: undefined,
    parentStep: undefined,
};
